import { useEffect, useRef } from 'react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { markStoryCompleted, isStoryCompleted } from '../utils/readingPoints';
import { sendReadingHeartbeat } from '../utils/webPush';

const STORAGE_KEY = 'dl_continue_reading_v1';
// How long the page must stay hidden before we fire the notification.
// This guarantees we never fire on quick tab-switch / channel-switch transitions
// or short app context changes — only on a real "user is gone" hide.
const HIDDEN_GRACE_MS = 3500;
// When the user has scrolled this far through the article body, we count
// it as "completed" and award reading points. Slightly under 100 to give
// a small grace zone for users who stop just shy of the very last line.
const COMPLETION_THRESHOLD = 95;

/**
 * ContinueReadingBeacon
 * ───────────────────────────────────────────────
 * Saves the current article to localStorage and fires a "Continue reading"
 * notification ONLY when the user is truly outside the app (phone locked,
 * switched to another app, minimized the browser) for longer than a short
 * grace period.
 *
 * Also computes a "read progress" percentage scoped to the article body
 * element (NOT the whole page — that includes footer / related stories
 * and would never hit 100%). Once the user crosses COMPLETION_THRESHOLD
 * the story is marked completed and POINTS_PER_STORY are awarded via the
 * readingPoints util (per-user, idempotent).
 *
 * Props:
 *   slug          — story slug (required)
 *   title         — story title (required for notif body)
 *   channelLabel  — "Dreams League" | "FLYT Studios" | etc.
 *   url           — article URL (required)
 *   image         — cover image (optional)
 *   targetSelector — CSS selector for the article body element used to
 *                    compute progress. Defaults to 'article' (StoryPage's
 *                    outer article wrapper).
 */
const ContinueReadingBeacon = ({
  slug, title, channelLabel = 'Dreams League', url, image,
  targetSelector = 'article',
}) => {
  const { user } = useUserAuth();
  const userId = user?.user_id || null;
  const firedRef = useRef(false);
  const pendingTimerRef = useRef(null);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!slug || !title || !url) return undefined;
    // Reset per-story completion flag — but seed it from storage so the
    // green "Completed" state shows immediately on revisit.
    completedRef.current = isStoryCompleted(userId, slug);

    // Read current scroll progress (0–100), scoped to the article body
    // when we can find one. Falls back to the whole-document calc so the
    // beacon still works on pages without an <article> element.
    const computeProgress = () => {
      try {
        const target = targetSelector ? document.querySelector(targetSelector) : null;
        if (target) {
          const rect = target.getBoundingClientRect();
          const vh = window.innerHeight;
          if (rect.height <= 0) return 0;
          // Distance from the TOP of the article to the BOTTOM of the viewport,
          // expressed as a % of the article's total height. Hits 100 the moment
          // the article's bottom edge is at or above the viewport bottom.
          const progressed = (vh - rect.top) / rect.height;
          return Math.max(0, Math.min(100, Math.round(progressed * 100)));
        }
        const docH = document.documentElement.scrollHeight - window.innerHeight;
        if (docH <= 0) return 100;
        return Math.max(0, Math.min(100, Math.round((window.scrollY / docH) * 100)));
      } catch { return 0; }
    };

    const persist = (progress) => {
      const pct = typeof progress === 'number' ? progress : computeProgress();
      try {
        const completed = pct >= COMPLETION_THRESHOLD || completedRef.current;
        const entry = {
          slug, title, channelLabel, url,
          image: image || null,
          ts: Date.now(),
          scrollPercent: pct,
          completed,
        };
        // 1. Save the LAST-VIEWED entry (used by HomepageLiveActivity / pill).
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
        // 2. Maintain a small history list of up to 12 items (deduped by slug)
        //    so the Watch History tab + Continue Watching row can show multiple.
        const HIST_KEY = 'dl_watch_history_v1';
        let history = [];
        try { history = JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { /* ignore */ }
        history = history.filter((h) => h && h.slug !== slug);
        history.unshift(entry);
        localStorage.setItem(HIST_KEY, JSON.stringify(history.slice(0, 12)));
      } catch { /* private mode */ }
    };

    persist();
    let lastSavedAt = 0;
    let lastHeartbeatAt = 0;
    // Fire an immediate heartbeat so the backend knows the user is here
    // (no-op if push not subscribed). Subsequent heartbeats are throttled
    // to once every 30s while they keep scrolling.
    sendReadingHeartbeat({ slug, title, image, url, progress: computeProgress() });
    const onScroll = () => {
      const now = Date.now();
      const pct = computeProgress();
      // Always check completion threshold even if we throttled the save —
      // hitting 100 is the most important moment of the whole flow.
      if (!completedRef.current && pct >= COMPLETION_THRESHOLD) {
        completedRef.current = true;
        markStoryCompleted(userId, {
          slug, title, image: image || undefined, channelLabel, url,
        });
        sendReadingHeartbeat({ slug, title, image, url, progress: 100 });
        // Persist immediately so the green "Completed" state shows right
        // away in any open Continue Watching row.
        persist(pct);
        return;
      }
      if (now - lastHeartbeatAt > 30_000) {
        lastHeartbeatAt = now;
        sendReadingHeartbeat({ slug, title, image, url, progress: pct });
      }
      if (now - lastSavedAt < 1500) return; // throttle to once every 1.5s
      lastSavedAt = now;
      persist(pct);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const fireNotification = () => {
      if (firedRef.current) return;
      if (document.visibilityState !== 'hidden') return;
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      try {
        const n = new Notification(`${channelLabel} · Continue reading`, {
          body: title,
          icon: '/favicon.ico',
          tag: `continue-reading-${slug}`,
          renotify: false,
          silent: true,
          data: { url },
        });
        n.onclick = (ev) => {
          ev.preventDefault();
          try { window.focus(); } catch { /* ignore */ }
          try { window.open(url, '_self'); } catch { /* ignore */ }
          n.close();
        };
        firedRef.current = true;
      } catch { /* swallow */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = setTimeout(fireNotification, HIDDEN_GRACE_MS);
      } else {
        if (pendingTimerRef.current) {
          clearTimeout(pendingTimerRef.current);
          pendingTimerRef.current = null;
        }
        firedRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('scroll', onScroll);
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, [slug, title, channelLabel, url, image, targetSelector, userId]);

  return null;
};

export default ContinueReadingBeacon;
